import { lazy, Suspense, useEffect } from "react";
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Layout } from "@/components/Layout";
import { useAuth } from "@/hooks/useAuth";
import { tryRestoreSession } from "@/lib/auth";
import { initTheme } from "@/lib/theme";
import { Login } from "@/routes/Login";

// Lazy-load every authenticated route so initial load only ships:
//  - the login page,
//  - the auth/layout/theme plumbing,
//  - vendor chunks (react / radix / tanstack-query).
//
// Route-specific deps (Recharts on Dashboard + WarReports, ScrollArea on
// WarReports' war strip, etc.) come down only when the user navigates
// to that route. Vite splits each lazy import into its own chunk
// automatically.
//
// The routes use named exports; the .then(m => ({ default: m.X })) shim
// adapts them to React.lazy's "module with default export" contract.
const Admin = lazy(() =>
  import("@/routes/Admin").then((m) => ({ default: m.Admin })),
);
const Dashboard = lazy(() =>
  import("@/routes/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const Enroll = lazy(() =>
  import("@/routes/Enroll").then((m) => ({ default: m.Enroll })),
);
const Members = lazy(() =>
  import("@/routes/Members").then((m) => ({ default: m.Members })),
);
const Settings = lazy(() =>
  import("@/routes/Settings").then((m) => ({ default: m.Settings })),
);
const Stats = lazy(() =>
  import("@/routes/Stats").then((m) => ({ default: m.Stats })),
);
const WarReports = lazy(() =>
  import("@/routes/WarReports").then((m) => ({ default: m.WarReports })),
);
const WarDashboard = lazy(() =>
  import("@/routes/WarDashboard").then((m) => ({ default: m.WarDashboard })),
);
const MemberTrend = lazy(() =>
  import("@/routes/MemberTrend").then((m) => ({ default: m.MemberTrend })),
);
const MyContributions = lazy(() =>
  import("@/routes/MyContributions").then((m) => ({
    default: m.MyContributions,
  })),
);
const Nags = lazy(() => import("@/routes/Nags").then((m) => ({ default: m.Nags })));

// Apply persisted theme before React paints.
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reasonable defaults for an analytics dashboard: cache for a minute,
      // refetch on focus so the user always gets fresh data after switching
      // tabs, but don't hammer the API on every interaction.
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

function ProtectedShell() {
  const auth = useAuth();
  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Restoring session…
      </div>
    );
  }
  if (!auth.user) {
    return <Navigate to="/" replace />;
  }
  return <Layout />;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  if (auth.loading) return null;
  if (auth.user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Suspense fallback while a route chunk is fetched. Kept intentionally
 * minimal — a route chunk over a warm CDN cache lands in <100ms most of
 * the time; only worth showing a spinner if it's actually noticeable.
 */
function RouteFallback() {
  return (
    <div className="py-12 text-center text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

export function App() {
  useEffect(() => {
    void tryRestoreSession();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/* HashRouter keeps deep-links working on the S3+CloudFront static
          deploy without server-side rewrites — same approach as the
          legacy Svelte app. */}
      <HashRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route
              path="/"
              element={
                <GuestRoute>
                  <Login />
                </GuestRoute>
              }
            />
            <Route element={<ProtectedShell />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/war" element={<WarDashboard />} />
              {/* Old paths from the alpha — kept as redirects so any open
                  tabs / bookmarks land on the new consolidated page. */}
              <Route
                path="/intel"
                element={<Navigate to="/war" replace />}
              />
              <Route
                path="/war-dashboard"
                element={<Navigate to="/war" replace />}
              />
              <Route path="/me" element={<MyContributions />} />
              <Route path="/war-reports" element={<WarReports />} />
              <Route
                path="/war-reports/members/:userId"
                element={<MemberTrend />}
              />
              <Route path="/members" element={<Members />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/nags" element={<Nags />} />
              <Route path="/enroll" element={<Enroll />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/stats" element={<Stats />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </HashRouter>
    </QueryClientProvider>
  );
}
